import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { PrecheckResult } from '../types';

interface PrecheckValidationResultsProps {
  result: PrecheckResult;
}

export const PrecheckValidationResults: FC<PrecheckValidationResultsProps> = ({ result }) => {
  const { t } = useTranslation();

  if (!result) {
    return null;
  }

  const hasIssues =
    (result.missingFields && Object.keys(result.missingFields).length > 0) ||
    (result.recommendedUploads && result.recommendedUploads.length > 0);

  return (
    <div className="space-y-6">
      {hasIssues && result.documentsScanned && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-day-neutral-dark mb-3">
            {t('package-creation.reports.documents-scanned', 'Documents Scanned')}
          </h2>
          <p className="text-day-neutral-subtle mb-2">
            {t('package-creation.reports.scanned-count', 'Total: {{count}} files', {
              count: result.documentsScanned.count,
            })}
          </p>
          {result.documentsScanned.files && result.documentsScanned.files.length > 0 && (
            <ul className="list-disc list-inside space-y-1 text-sm text-day-neutral-subtle">
              {result.documentsScanned.files.map((file) => (
                <li key={file}>{file}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {result.missingFields && Object.keys(result.missingFields).length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-orange-900 mb-3">
            {t('package-creation.reports.missing-fields', 'Missing Fields')}
          </h2>
          <div className="space-y-3">
            {Object.entries(result.missingFields).map(([section, fields]) => (
              <div key={section}>
                <h3 className="font-medium text-orange-800 mb-1">{section}</h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-orange-700 ml-4">
                  {fields.map((field) => (
                    <li key={field}>{field}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {result.recommendedUploads && result.recommendedUploads.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-blue-900 mb-3">
            {t('package-creation.reports.recommended-uploads', 'Recommended Uploads')}
          </h2>
          <ul className="list-disc list-inside space-y-2 text-sm text-blue-800">
            {result.recommendedUploads.map((upload) => (
              <li key={upload.documentName}>
                <span className="font-medium">{upload.documentName}</span>
                {upload.coversFields.length > 0 && (
                  <span className="text-blue-600 ml-1">
                    ({t('package-creation.reports.covers', 'covers')}:{' '}
                    {upload.coversFields.join(', ')})
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
